/* Base styles */
.base {
  --button-bg-hover: var(--button-bg);
  --button-bg-focused: var(--button-bg);
  --button-bg-pressed: var(--button-bg);
  --button-disabled-opacity: 50%;

  display: inline-flex;
  gap: var(--ism-spacing-xs);
  align-items: center;
  justify-content: center;
  font-family: inherit;
  color: var(--button-fg, currentColor);
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  background-color: var(--button-bg, transparent);

  /* Keep button height stable across filled and outlined variants. */
  border-color: var(--button-border-color, transparent);
  border-style: solid;
  border-width: 1px;
  border-radius: var(--button-radius, var(--ism-radius-full));
  transition-duration: 0.2s;
  transition-property: all;
}

.full {
  --button-radius: var(--ism-radius-full);
}

.circle {
  --button-radius: var(--ism-radius-circle);
}

/* Appearance variants - Color modifiers only */
.filled {
  --button-fg: var(--ism-color-on-primary);
  --button-bg: var(--ism-color-primary);
  --button-bg-hover: color-mix(
    in srgb,
    var(--ism-color-primary) 85%,
    var(--ism-color-on-primary-opacity-low) 15%
  );
  --button-bg-focused: color-mix(
    in srgb,
    var(--ism-color-primary) 80%,
    var(--ism-color-on-primary-opacity-mid) 20%
  );
  --button-bg-pressed: color-mix(
    in srgb,
    var(--ism-color-primary) 75%,
    var(--ism-color-on-primary-opacity-high) 25%
  );
  --button-fg-disabled: var(--ism-color-on-surface);
  --button-bg-disabled: var(--ism-color-on-surface-opacity-mid);
}

.filled[data-hovered] {
  background-color: var(--button-bg-hover);
}

.filled:focus-visible {
  background-color: var(--button-bg-focused);
}

.filled[data-pressed],
.filled:active {
  background-color: var(--button-bg-pressed);
}

.filled[data-disabled] {
  color: color-mix(
    in srgb,
    var(--button-fg-disabled) var(--button-disabled-opacity),
    transparent
  );
  pointer-events: none;
  background-color: var(--button-bg-disabled);
}

.outlined {
  --button-fg: var(--ism-color-primary);
  --button-border-color: var(--ism-color-primary);
  --button-fg-interactive: var(--ism-color-on-primary);
  --button-bg-hover: color-mix(
    in srgb,
    var(--ism-color-primary) 85%,
    var(--ism-color-on-primary-opacity-low) 15%
  );
  --button-bg-focused: color-mix(
    in srgb,
    var(--ism-color-primary) 80%,
    var(--ism-color-on-primary-opacity-mid) 20%
  );
  --button-bg-pressed: color-mix(
    in srgb,
    var(--ism-color-primary) 75%,
    var(--ism-color-on-primary-opacity-high) 25%
  );
  --button-fg-disabled: var(--ism-color-on-surface);
  --button-border-disabled: var(--ism-color-outline-variant);
}

.outlined[data-hovered] {
  color: var(--button-fg-interactive);
  background-color: var(--button-bg-hover);
}

.outlined:focus-visible {
  color: var(--button-fg-interactive);
  background-color: var(--button-bg-focused);
}

.outlined[data-pressed],
.outlined:active {
  color: var(--button-fg-interactive);
  background-color: var(--button-bg-pressed);
}

.outlined[data-disabled] {
  color: color-mix(
    in srgb,
    var(--button-fg-disabled) var(--button-disabled-opacity),
    transparent
  );
  pointer-events: none;
  border-color: var(--button-border-disabled);
}

.text {
  --button-fg: var(--ism-color-on-surface);
  --button-fg-interactive: var(--ism-color-on-primary-container);
  --button-bg-hover: var(--ism-color-on-surface-opacity-low);
  --button-bg-focused: var(--ism-color-on-surface-opacity-mid);
  --button-bg-pressed: var(--ism-color-on-surface-opacity-high);
  --button-fg-disabled: var(--ism-color-on-surface);
}

.text[data-hovered] {
  color: var(--button-fg-interactive);
  background-color: var(--button-bg-hover);
}

.text:focus-visible {
  color: var(--button-fg-interactive);
  background-color: var(--button-bg-focused);
}

.text[data-pressed],
.text:active {
  color: var(--button-fg-interactive);
  background-color: var(--button-bg-pressed);
}

.text[data-disabled] {
  color: color-mix(
    in srgb,
    var(--button-fg-disabled) var(--button-disabled-opacity),
    transparent
  );
  pointer-events: none;
}

.tonal {
  --button-fg: var(--ism-color-on-primary-container);
  --button-bg: var(--ism-color-primary-container);
  --button-bg-hover: color-mix(
    in srgb,
    var(--ism-color-primary-container) 85%,
    var(--ism-color-on-surface-opacity-low) 15%
  );
  --button-bg-focused: color-mix(
    in srgb,
    var(--ism-color-primary-container) 80%,
    var(--ism-color-on-surface-opacity-mid) 20%
  );
  --button-bg-pressed: color-mix(
    in srgb,
    var(--ism-color-primary-container) 75%,
    var(--ism-color-on-surface-opacity-high) 25%
  );
  --button-fg-disabled: var(--ism-color-on-surface);
  --button-bg-disabled: var(--ism-color-on-surface-opacity-low);
}

.tonal[data-hovered] {
  background-color: var(--button-bg-hover);
}

.tonal:focus-visible {
  background-color: var(--button-bg-focused);
}

.tonal[data-pressed],
.tonal:active {
  background-color: var(--button-bg-pressed);
}

.tonal[data-disabled] {
  color: color-mix(
    in srgb,
    var(--button-fg-disabled) var(--button-disabled-opacity),
    transparent
  );
  pointer-events: none;
  background-color: var(--button-bg-disabled);
}

.glass {
  --button-fg: var(--ism-color-on-glass);
  --button-bg: var(--ism-color-glass);
  --button-bg-hover: color-mix(
    in srgb,
    var(--ism-color-glass) 85%,
    var(--ism-color-on-surface-opacity-low) 15%
  );
  --button-bg-focused: color-mix(
    in srgb,
    var(--ism-color-glass) 80%,
    var(--ism-color-on-surface-opacity-mid) 20%
  );
  --button-bg-pressed: color-mix(
    in srgb,
    var(--ism-color-glass) 75%,
    var(--ism-color-on-surface-opacity-high) 25%
  );
  --button-fg-disabled: var(--ism-color-glass);
  --button-bg-disabled: var(--ism-color-on-glass);

  backdrop-filter: blur(10px);
}

.glass[data-hovered] {
  background-color: var(--button-bg-hover);
}

.glass:focus-visible {
  background-color: var(--button-bg-focused);
}

.glass[data-pressed],
.glass:active {
  background-color: var(--button-bg-pressed);
}

.glass[data-disabled] {
  color: color-mix(
    in srgb,
    var(--button-fg-disabled) var(--button-disabled-opacity),
    transparent
  );
  pointer-events: none;
  background-color: var(--button-bg-disabled);
}

.error {
  --button-fg: var(--ism-color-on-error);
  --button-bg: var(--ism-color-error);
  --button-bg-hover: color-mix(
    in srgb,
    var(--ism-color-error) 85%,
    var(--ism-color-on-primary-opacity-low) 15%
  );
  --button-bg-focused: color-mix(
    in srgb,
    var(--ism-color-error) 80%,
    var(--ism-color-on-primary-opacity-mid) 20%
  );
  --button-bg-pressed: color-mix(
    in srgb,
    var(--ism-color-error) 75%,
    var(--ism-color-on-primary-opacity-high) 25%
  );
  --button-fg-disabled: var(--ism-color-on-surface);
  --button-bg-disabled: var(--ism-color-on-surface-opacity-mid);
}

.error[data-hovered] {
  background-color: var(--button-bg-hover);
}

.error:focus-visible {
  background-color: var(--button-bg-focused);
}

.error[data-pressed],
.error:active {
  background-color: var(--button-bg-pressed);
}

.error[data-disabled] {
  color: color-mix(
    in srgb,
    var(--button-fg-disabled) var(--button-disabled-opacity),
    transparent
  );
  pointer-events: none;
  background-color: var(--button-bg-disabled);
}

.success {
  --button-fg: var(--ism-color-on-success);
  --button-bg: var(--ism-color-success);
  --button-bg-hover: color-mix(
    in srgb,
    var(--ism-color-success) 85%,
    var(--ism-color-on-primary-opacity-low) 15%
  );
  --button-bg-focused: color-mix(
    in srgb,
    var(--ism-color-success) 80%,
    var(--ism-color-on-primary-opacity-mid) 20%
  );
  --button-bg-pressed: color-mix(
    in srgb,
    var(--ism-color-success) 75%,
    var(--ism-color-on-primary-opacity-high) 25%
  );
  --button-fg-disabled: var(--ism-color-on-surface);
  --button-bg-disabled: var(--ism-color-on-surface-opacity-mid);
}

.success[data-hovered] {
  background-color: var(--button-bg-hover);
}

.success:focus-visible {
  background-color: var(--button-bg-focused);
}

.success[data-pressed],
.success:active {
  background-color: var(--button-bg-pressed);
}

.success[data-disabled] {
  color: color-mix(
    in srgb,
    var(--button-fg-disabled) var(--button-disabled-opacity),
    transparent
  );
  pointer-events: none;
  background-color: var(--button-bg-disabled);
}

.medium {
  padding: var(--ism-spacing-s) var(--ism-spacing-m);
  font-size: var(--ism-font-size-body-m);
}

.small {
  padding: var(--ism-spacing-xs) var(--ism-spacing-s);
  font-size: var(--ism-font-size-body-s);
}

.fullWidth {
  width: 100%;
}

.noWrap {
  white-space: nowrap;
}
